/**
 * ORDER 23 Session 2 presentation model for CHART C.
 *
 * The Worker and rangeBox.ts own detection. This module only maps already-closed range bars onto
 * LWC's synthetic timeline and exposes the exact evidence each drawable receives. The measured
 * month normals are named because glow/cost are HYPOTHESIS display grammar, never execution.
 */
import {
  detectDeltaFlips,
  detectRangeBreaks,
  findActiveRangeBox,
  partitionRangeBarsByStudySession,
  studySessionDate,
  type RangeBox,
  type RangeBreak,
  type RangeBoxBar,
} from "./rangeBox.ts";

export const RANGE_NORMAL_CONTRACTS = 359;
export const RANGE_NORMAL_MINUTES = 3;
export const RANGE_SOFT_GLOW = 2;
export const RANGE_HOT_GLOW = 4;
export const RANGE_INITIATIVE_CONTRACTS = RANGE_NORMAL_CONTRACTS * 2;
export const RANGE_INITIATIVE_MAX_MINUTES = RANGE_NORMAL_MINUTES / 2;
export const RANGE_SYNTHETIC_EPOCH_S = Date.UTC(2000, 0, 3) / 1_000;
export const RANGE_STRUCTURE_FLOOR = 6;

export interface RangeChartBar extends RangeBoxBar {
  readonly n_signed: number;
  readonly side: string;
  readonly effort: number | null;
  readonly absorbed?: boolean;
}

export interface RangeChartLevel {
  readonly price: number;
  readonly label: string;
  readonly kind: "magnet" | "gammaflip";
}

export interface RangeChartOptions {
  readonly ticks: 8 | 12;
  readonly cumulativeBaseline: number;
  readonly widthMode?: "minutes" | "equal";
  readonly levels?: readonly RangeChartLevel[];
  /** The served venue verdict; only CLOSED changes the displayed session. */
  readonly sessionVerdict?: string | null;
  /** Clock used only for the no-verdict closed-day fallback. */
  readonly nowMs?: number;
}

export interface RangeChartPoint extends RangeChartBar {
  readonly index: number;
  readonly syntheticSlot: number;
  readonly syntheticStartTime: number;
  readonly syntheticTime: number;
  readonly cumulativeCvd: number;
  readonly cost: number;
  readonly absorptionScore: number;
  readonly glow: "none" | "soft" | "hot";
  readonly initiative: boolean;
  readonly absorbed: boolean;
}

type Marker = { index: number; time: number };

export interface RangeEdgeChipInput {
  readonly id: "range" | "rangeDe" | "rangeCvd";
  readonly desiredTop: number;
}

export interface RangeEdgeChipPosition extends RangeEdgeChipInput {
  readonly top: number;
}

/** Resolve one right-edge column without changing the chips' data-order identity. */
export function resolveRangeEdgeChipPositions(
  chips: readonly RangeEdgeChipInput[],
  containerHeight: number,
  gap = 18,
  padding = 4,
): RangeEdgeChipPosition[] {
  if (![containerHeight, gap, padding].every(Number.isFinite) || containerHeight <= 0 || gap <= 0 || padding < 0)
    throw new Error("range edge-chip geometry must be finite and positive");
  const maxTop = Math.max(padding, containerHeight - gap - padding);
  const ordered = chips
    .map((chip) => ({ ...chip, desiredTop: Math.max(padding, Math.min(maxTop, chip.desiredTop)) }))
    .sort((a, b) => a.desiredTop - b.desiredTop);
  const tops: number[] = [];
  for (const chip of ordered)
    tops.push(tops.length ? Math.max(chip.desiredTop, tops.at(-1)! + gap) : chip.desiredTop);
  const overflow = Math.max(0, (tops.at(-1) ?? padding) - maxTop);
  const shifted = tops.map((top) => top - overflow);
  for (let index = shifted.length - 2; index >= 0; index -= 1)
    shifted[index] = Math.min(shifted[index], shifted[index + 1] - gap);
  const byId = new Map(ordered.map((chip, index) => [chip.id, Math.max(padding, shifted[index]) ]));
  return chips.map((chip) => ({ ...chip, top: byId.get(chip.id) ?? padding }));
}

export interface RangeChartReadback {
  rangeBar: RangeChartPoint[];
  rangeDelta: Array<{ time: number; value: number }>;
  rangeHalo: Array<{ time: number; value: number; score: number; glow: "soft" | "hot" }>;
  rangeCvd: Array<{ time: number; value: number }>;
  rangeLevels: RangeChartLevel[];
  rangeBox: Array<{ startIndex: number; endIndex: number; lower: number; upper: number; live: boolean }>;
  rangeBreak: Array<RangeBreak & { time: number }>;
  rangeFlip: Marker[];
  rangeAbsorbed: Marker[];
  rangeCost: Array<{ time: number; value: number }>;
}

export interface RangeChartModel {
  readonly points: RangeChartPoint[];
  readonly breaks: RangeBreak[];
  readonly activeBox: RangeBox | null;
  readonly readback: RangeChartReadback;
}

export interface RangeStructureLabel {
  readonly id: string;
  readonly left: number;
  readonly top: number;
  readonly width: number;
  readonly height: number;
  readonly text: string;
}

/**
 * Keep the first structure verdict whose label rectangle lies inside the pane and does not collide
 * with an earlier one. A box scrolled off the pane still has a finite LWC coordinate; laid out there,
 * its label widened the document to x 5798 (FOCUS-FIT-1 box 4, FINDING 13).
 */
export function cullRangeStructureLabels(
  candidates: readonly RangeStructureLabel[],
  paneWidth: number,
): RangeStructureLabel[] {
  if (!Number.isFinite(paneWidth) || paneWidth < 0) {
    throw new Error(`range structure labels need a finite pane width, got ${paneWidth}`);
  }
  const placed: RangeStructureLabel[] = [];
  for (const candidate of candidates) {
    if (![candidate.left, candidate.top, candidate.width, candidate.height].every(Number.isFinite) ||
        candidate.width < 0 || candidate.height < 0) {
      throw new Error(`range structure label ${candidate.id} has invalid geometry`);
    }
    if (candidate.left < 0 || candidate.left + candidate.width > paneWidth) continue;
    const overlaps = placed.some((prior) =>
      candidate.left < prior.left + prior.width && candidate.left + candidate.width > prior.left &&
      candidate.top < prior.top + prior.height && candidate.top + candidate.height > prior.top,
    );
    if (!overlaps) placed.push(candidate);
  }
  return placed;
}

export function closedRangeSessionDate(
  bars: readonly RangeChartBar[],
  options: Pick<RangeChartOptions, "sessionVerdict" | "nowMs"> = {},
): string | null {
  const sessions = partitionRangeBarsByStudySession(bars);
  const newest = sessions.at(-1);
  if (!newest) return null;
  if (options.sessionVerdict === "CLOSED") return newest.sessionDate;
  if (options.sessionVerdict !== null && options.sessionVerdict !== undefined) return null;
  if (options.nowMs !== undefined && studySessionDate(options.nowMs) > newest.sessionDate)
    return newest.sessionDate;
  return null;
}

/** The ⓘ readback names the detector floor, and names the short-session refusal without hiding ink. */
export function rangeStructureNotice(points: readonly RangeChartPoint[]): string | null {
  const newest = points.at(-1);
  if (!newest) return null;
  const newestDate = studySessionDate(newest.t1);
  const count = points.filter((point) => studySessionDate(point.t1) === newestDate).length;
  return count < RANGE_STRUCTURE_FLOOR
    ? `structure: ${count} bars, below the detector's floor`
    : `structure: ${count} bars · detector floor ${RANGE_STRUCTURE_FLOOR}`;
}

function assertBars(bars: readonly RangeChartBar[]): void {
  let priorClose = Number.NEGATIVE_INFINITY;
  for (const [index, bar] of bars.entries()) {
    for (const field of ["t0", "t1", "minutes", "o", "h", "l", "c", "contracts", "n_signed", "max_bar"] as const) {
      if (!Number.isFinite(bar[field])) throw new Error(`range chart bar ${index} has non-finite ${field}`);
    }
    if (bar.t1 < bar.t0) throw new Error(`range chart bar ${index} closes before it opens`);
    if (bar.t1 < priorClose) throw new Error(`range chart bars are not point-in-time ordered at ${index}`);
    const elapsed = Math.max(1, Math.round((bar.t1 - bar.t0) / 60_000));
    if (bar.minutes !== elapsed) {
      throw new Error(`range chart bar ${index} minutes mismatch: served ${bar.minutes}, elapsed ${elapsed}`);
    }
    priorClose = bar.t1;
  }
}

export function buildRangeChartModel(
  bars: readonly RangeChartBar[],
  options: RangeChartOptions,
): RangeChartModel {
  assertBars(bars);
  if (!Number.isFinite(options.cumulativeBaseline))
    throw new Error("range chart requires a finite session CVD baseline");
  const widthMode = options.widthMode ?? "minutes";
  const sessions = partitionRangeBarsByStudySession(bars);
  const newestSession = sessions.at(-1);
  const newestStartIndex = newestSession ? bars.length - newestSession.bars.length : 0;
  const closedSessionDate = closedRangeSessionDate(bars, options);
  const closed = closedSessionDate !== null;
  const drawnStartIndex = closed ? newestStartIndex : 0;
  const drawnBars = bars.slice(drawnStartIndex);
  const drawnBaseline = options.cumulativeBaseline + (closed
    ? bars.slice(0, newestStartIndex).reduce((sum, bar) => sum + bar.contracts, 0)
    : 0);
  let slot = 0;
  let cumulativeCvd = drawnBaseline;
  const points = drawnBars.map((bar, index): RangeChartPoint => {
    const occupiedSlots = widthMode === "minutes" ? Math.max(1, bar.minutes) : 1;
    const startSlot = slot + 1;
    slot += occupiedSlots;
    cumulativeCvd += bar.contracts;
    const cost = Math.abs(bar.contracts) / RANGE_NORMAL_CONTRACTS;
    const absorptionScore = cost * (bar.minutes / RANGE_NORMAL_MINUTES);
    const initiative = Math.abs(bar.contracts) >= RANGE_INITIATIVE_CONTRACTS &&
      bar.minutes <= RANGE_INITIATIVE_MAX_MINUTES;
    const glow = initiative ? "none" : absorptionScore >= RANGE_HOT_GLOW ? "hot" :
      absorptionScore >= RANGE_SOFT_GLOW ? "soft" : "none";
    return {
      ...bar,
      index,
      syntheticSlot: slot,
      syntheticStartTime: RANGE_SYNTHETIC_EPOCH_S + startSlot * 60,
      syntheticTime: RANGE_SYNTHETIC_EPOCH_S + slot * 60,
      cumulativeCvd,
      cost,
      absorptionScore,
      glow,
      initiative,
      absorbed: Math.sign(bar.c - bar.o) !== 0 &&
        Math.sign(bar.contracts) !== Math.sign(bar.c - bar.o) &&
        Math.abs(bar.contracts) >= RANGE_NORMAL_CONTRACTS * 2,
    };
  });

  const structureBars = newestSession?.bars ?? [];
  const structureOffset = closed ? 0 : newestStartIndex;
  const mapStructureIndex = (index: number) => index + structureOffset;
  const mapBox = (box: RangeBox): RangeBox => ({
    ...box,
    startIndex: mapStructureIndex(box.startIndex),
    endIndex: mapStructureIndex(box.endIndex),
  });
  const breaks = detectRangeBreaks(structureBars, {
    boxBars: RANGE_STRUCTURE_FLOOR, rangeTicks: options.ticks, tickSize: .25,
  }).map((entry) => ({ ...entry, box: mapBox(entry.box), breakIndex: mapStructureIndex(entry.breakIndex) }));
  const activeDetected = findActiveRangeBox(structureBars, {
    boxBars: RANGE_STRUCTURE_FLOOR, rangeTicks: options.ticks, tickSize: .25,
  });
  const active = activeDetected ? mapBox(activeDetected) : null;
  const timeForIndex = (index: number) => points[index]?.syntheticTime ?? RANGE_SYNTHETIC_EPOCH_S;
  let sessionOffset = 0;
  const flips = partitionRangeBarsByStudySession(drawnBars).flatMap((session) => {
    const sessionPoints = points.slice(sessionOffset, sessionOffset + session.bars.length);
    sessionOffset += session.bars.length;
    return detectDeltaFlips(sessionPoints.map((point) => ({
      t0: point.t0, t1: point.t1, delta: point.contracts,
      cvd: point.cumulativeCvd, close: point.c,
    }))).flatMap((flip) => {
      const localIndex = sessionPoints.findIndex((point) => point.t1 === flip.t);
      const index = localIndex < 0 ? -1 : sessionOffset - session.bars.length + localIndex;
      return index < 0 ? [] : [{ index, time: timeForIndex(index) }];
    });
  });
  const boxes = breaks.map(({ box }) => ({
    startIndex: box.startIndex, endIndex: box.endIndex, lower: box.lower, upper: box.upper, live: false,
  }));
  if (active) boxes.push({
    startIndex: active.startIndex, endIndex: active.endIndex, lower: active.lower, upper: active.upper, live: true,
  });

  const readback: RangeChartReadback = {
    rangeBar: points,
    rangeDelta: points.map((point) => ({ time: point.syntheticTime, value: point.contracts })),
    rangeHalo: points.flatMap((point) => point.glow === "none" ? [] : [{
      time: point.syntheticTime, value: point.contracts, score: point.absorptionScore, glow: point.glow,
    }]),
    rangeCvd: points.map((point) => ({ time: point.syntheticTime, value: point.cumulativeCvd })),
    rangeLevels: [...(options.levels ?? [])],
    rangeBox: boxes,
    rangeBreak: breaks.map((entry) => ({ ...entry, time: timeForIndex(entry.breakIndex) })),
    rangeFlip: flips,
    rangeAbsorbed: points.flatMap((point) => point.absorbed ? [{
      index: point.index, time: point.syntheticTime,
    }] : []),
    rangeCost: points.map((point) => ({ time: point.syntheticTime, value: point.cost })),
  };
  return { points, breaks, activeBox: active, readback };
}

/** Every occupied minute is submitted to LWC so duration is real logical width, not a timestamp gap. */
export function rangeLogicalSlots(points: readonly RangeChartPoint[]): Array<{ time: number; point?: RangeChartPoint }> {
  const byEnd = new Map(points.map((point) => [point.syntheticTime, point]));
  const last = points.at(-1)?.syntheticSlot ?? 0;
  return Array.from({ length: last }, (_, index) => {
    const time = RANGE_SYNTHETIC_EPOCH_S + (index + 1) * 60;
    return { time, ...(byEnd.has(time) ? { point: byEnd.get(time)! } : {}) };
  });
}
